import React, { useEffect, useRef, useState } from "react";
import "./HoverPlayCard.css";

// В один момент времени играет только одна карточка.
let activeStop = null;

/**
 * Карточка с постером, которая при наведении начинает проигрывать сам ролик
 * (без звука, по кругу). Убрал курсор — плеер уничтожается, постер возвращается.
 *
 * props:
 *   poster       URL постера / скриншота (обязателен)
 *   hlsUrl       master.m3u8; если нет — карточка ведёт себя как обычный <img>
 *   Hls          конструктор hls.js (import Hls from "hls.js")
 *   delay        задержка перед стартом, мс (350) — чтобы не дёргать сеть при пролёте курсора
 *   startAt      с какой секунды начинать (0) или доля длительности, если < 1 и > 0 (напр. 0.1)
 *   loop         зациклить (true)
 *   showProgress тонкая полоска прогресса внизу (true)
 *   disabled     не запускать (загрузка, обработка, архив и т. п.)
 *   active       управляемый режим: true — играть, false — стоп. Если передан, собственные
 *                onMouseEnter/onMouseLeave карточки не используются — наведение отслеживает
 *                родитель (удобно, когда поверх карточки лежат другие блоки и события до неё
 *                не доходят). Задержка delay действует и здесь.
 *   alt, className, style, imgStyle
 *   children     оверлей поверх (бейджи, меню) — рендерится сверху как есть
 */
export default function HoverPlayCard({
  poster,
  hlsUrl,
  Hls,
  delay = 350,
  startAt = 0,
  loop = true,
  showProgress = true,
  disabled = false,
  alt = "",
  className = "",
  style,
  imgStyle,
  children,
  active,
}) {
  const [armed, setArmed] = useState(false); // курсор наведён и задержка прошла
  const [playing, setPlaying] = useState(false); // видео реально пошло — можно прятать постер
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const canPlay = !!hlsUrl && !disabled && !prefersReducedMotion();

  const controlled = active !== undefined;

  const arm = () => {
    if (!canPlay) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setArmed(true), delay);
  };
  const disarm = () => {
    clearTimeout(timerRef.current);
    setArmed(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Управляемый режим: наведение отслеживает родитель и передаёт active.
  useEffect(() => {
    if (!controlled) return;
    if (active) arm();
    else disarm();
  }, [controlled, active, canPlay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Жизненный цикл плеера: создаётся при armed=true, уничтожается при armed=false.
  useEffect(() => {
    if (!armed || !canPlay) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    activeStop?.();
    let hls = null;
    let alive = true;

    const stop = () => {
      alive = false;
      if (activeStop === stop) activeStop = null;
      if (hls) {
        hls.destroy();
        hls = null;
      }
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
    activeStop = stop;

    const onLoadedMeta = () => {
      const d = video.duration;
      if (isFinite(d) && startAt > 0)
        video.currentTime =
          startAt < 1 ? d * startAt : Math.min(startAt, d - 1);
      video.play().catch(() => {});
    };
    const onPlaying = () => alive && setPlaying(true);
    const onTime = () => {
      if (
        alive &&
        showProgress &&
        isFinite(video.duration) &&
        video.duration > 0
      ) {
        setProgress(video.currentTime / video.duration);
      }
    };
    video.addEventListener("loadedmetadata", onLoadedMeta);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("timeupdate", onTime);

    if (Hls && Hls.isSupported()) {
      hls = new Hls({
        capLevelToPlayerSize: true, // карточка маленькая — возьмёт низкое качество
        startLevel: 0,
        maxBufferLength: 10,
        backBufferLength: 0,
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
    }

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMeta);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("timeupdate", onTime);
      stop();
      setPlaying(false);
      setProgress(0);
    };
  }, [armed, canPlay, hlsUrl, Hls, startAt, showProgress]);

  return (
    <div
      className={`hpc ${playing ? "hpc--playing" : ""} ${className}`}
      style={style}
      onMouseEnter={controlled ? undefined : arm}
      onMouseLeave={controlled ? undefined : disarm}
      onFocus={controlled ? undefined : arm}
      onBlur={controlled ? undefined : disarm}
    >
      <img
        className="hpc__poster"
        src={poster}
        alt={alt}
        style={imgStyle}
        draggable={false}
      />
      {armed && canPlay && (
        <video
          ref={videoRef}
          className="hpc__video"
          muted
          playsInline
          loop={loop}
          preload="auto"
          disablePictureInPicture
          tabIndex={-1}
        />
      )}
      {armed && canPlay && !playing && <div className="hpc__spinner" />}
      {playing && showProgress && (
        <div className="hpc__progress">
          <div
            className="hpc__progress-fill"
            style={{ transform: `scaleX(${progress})` }}
          />
        </div>
      )}
      {children}
    </div>
  );
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}
